import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  RadioGroup,
  Radio,
  Spinner,
  Text,
  MessageBar,
  MessageBarBody,
  TabList,
  Tab,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { list365Groups } from '@/services/api/admin';
import { useSetProjectGroup } from '@/hooks/useAdmin';
import type { MissingGroupProject } from '@/services/api/endpoints';

// Derive a plausible mailNickname from the project number (Graph requires a
// mail-safe, unique-ish alias). Admin can edit before creating.
function defaultNickname(number: string): string {
  const base = number
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `projet-${base || 'sans-numero'}`;
}

const useStyles = makeStyles({
  content: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  results: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    maxHeight: '220px',
    overflowY: 'auto',
    paddingRight: tokens.spacingHorizontalXS,
  },
  groupOpt: { display: 'flex', flexDirection: 'column' },
  groupMail: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  loading: { display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalM },
});

interface Props {
  project: MissingGroupProject;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectGroupDialog({ project, open, onOpenChange }: Props) {
  const styles = useStyles();
  const [mode, setMode] = useState<'bind' | 'create'>('bind');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(project.name);
  const [mailNickname, setMailNickname] = useState(defaultNickname(project.number));
  const [visibility, setVisibility] = useState<'Private' | 'Public'>('Private');
  const setGroup = useSetProjectGroup();

  const groups = useQuery({
    queryKey: ['admin', '365-groups', q],
    queryFn: () => list365Groups(q.trim() || undefined),
    enabled: open && mode === 'bind',
    staleTime: 30_000,
  });

  const errMsg = setGroup.error instanceof Error ? setGroup.error.message : null;
  const canSubmit =
    !setGroup.isPending &&
    (mode === 'bind' ? Boolean(selected) : displayName.trim().length > 0 && mailNickname.trim().length > 0);

  const submit = () => {
    if (mode === 'bind') {
      if (!selected) return;
      setGroup.mutate(
        { containerId: project.id, m365GroupId: selected },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      setGroup.mutate(
        {
          containerId: project.id,
          newGroup: {
            displayName: displayName.trim(),
            mailNickname: mailNickname.trim(),
            visibility,
          },
        },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_e, d) => onOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Groupe Microsoft 365 : {project.number}</DialogTitle>
          <DialogContent className={styles.content}>
            <Text className={styles.hint}>{project.name}</Text>

            <TabList selectedValue={mode} onTabSelect={(_e, d) => setMode(d.value as 'bind' | 'create')}>
              <Tab value="bind">Associer un groupe existant</Tab>
              <Tab value="create">Créer un nouveau groupe</Tab>
            </TabList>

            {mode === 'bind' ? (
              <>
                <Field label="Rechercher un groupe Microsoft 365">
                  <Input
                    value={q}
                    placeholder="Nom du groupe…"
                    onChange={(_e, d) => setQ(d.value)}
                  />
                </Field>
                {groups.isPending ? (
                  <div className={styles.loading}>
                    <Spinner size="tiny" label="Chargement…" />
                  </div>
                ) : (groups.data ?? []).length === 0 ? (
                  <Text className={styles.hint}>
                    Aucun groupe disponible (les groupes déjà rattachés à un espace/projet sont
                    masqués).
                  </Text>
                ) : (
                  <RadioGroup value={selected ?? ''} onChange={(_e, d) => setSelected(d.value)}>
                    <div className={styles.results}>
                      {(groups.data ?? []).map((g) => (
                        <Radio
                          key={g.id}
                          value={g.id}
                          label={
                            <span className={styles.groupOpt}>
                              <span>{g.displayName ?? g.id}</span>
                              {g.mail && <span className={styles.groupMail}>{g.mail}</span>}
                            </span>
                          }
                        />
                      ))}
                    </div>
                  </RadioGroup>
                )}
              </>
            ) : (
              <>
                <Field label="Nom d'affichage du groupe">
                  <Input value={displayName} onChange={(_e, d) => setDisplayName(d.value)} />
                </Field>
                <Field
                  label="Alias (mailNickname)"
                  hint="Utilisé pour l'adresse du groupe ; lettres/chiffres/tirets."
                >
                  <Input value={mailNickname} onChange={(_e, d) => setMailNickname(d.value)} />
                </Field>
                <Field label="Visibilité">
                  <RadioGroup
                    value={visibility}
                    onChange={(_e, d) => setVisibility(d.value as 'Private' | 'Public')}
                    layout="horizontal"
                  >
                    <Radio value="Private" label="Privé" />
                    <Radio value="Public" label="Public" />
                  </RadioGroup>
                </Field>
                <Text className={styles.hint}>
                  Vous serez propriétaire du groupe. La création peut être refusée par la
                  politique du tenant.
                </Text>
              </>
            )}

            {errMsg && (
              <MessageBar intent="error">
                <MessageBarBody>{errMsg}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)} disabled={setGroup.isPending}>
              Annuler
            </Button>
            <Button appearance="primary" onClick={submit} disabled={!canSubmit}>
              {setGroup.isPending
                ? mode === 'create'
                  ? 'Création…'
                  : 'Association…'
                : mode === 'create'
                  ? 'Créer et associer'
                  : 'Associer'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
